import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT, QUICK_LINKS } from "@/data/content";

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer
      data-testid="site-footer"
      className="mt-24 border-t border-[color:var(--adhd-border)]"
      style={{ backgroundColor: "#0f1e26", color: "#cfd8dc" }}
    >
      <div className="adhd-container py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span
              className="grid place-items-center h-11 w-11 rounded-2xl overflow-hidden bg-white"
            >
              <img
                src="/adhd-logo.jpeg"
                alt="ADHD School Bhadrak logo"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </span>
            <div>
              <p className="font-serif text-lg text-white leading-tight">
                {CONTACT.schoolName}
              </p>
              <p className="text-xs uppercase tracking-[0.18em] opacity-70">
                {CONTACT.shortName}
              </p>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed opacity-80 max-w-md">
            {t("brand.tagline")}
          </p>
          <p className="mt-3 text-xs italic opacity-60">{t("footer.builtBy")}</p>

          <div className="mt-6 flex items-center gap-3">
            {[
              { icon: Facebook, label: "Facebook" },
              { icon: Instagram, label: "Instagram" },
              { icon: Twitter, label: "Twitter" },
              { icon: Youtube, label: "YouTube" },
            ].map(({ icon: Icon, label }) => (
              <a
                key={label}
                href="#"
                data-testid={`social-${label.toLowerCase()}`}
                aria-label={label}
                className="grid place-items-center h-9 w-9 rounded-full border border-white/15 hover:bg-white/10 transition-colors"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-white text-sm uppercase tracking-[0.18em] font-semibold">
            {t("footer.quickLinks")}
          </h4>
          <ul className="mt-5 space-y-2.5 text-sm">
            {QUICK_LINKS.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.path}
                  data-testid={`footer-link-${link.label.toLowerCase()}`}
                  className="opacity-80 hover:opacity-100 hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-white text-sm uppercase tracking-[0.18em] font-semibold">
            {t("footer.contact")}
          </h4>
          <ul className="mt-5 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Phone size={15} className="mt-1 opacity-70" />
              <a
                href={`tel:${CONTACT.phoneTel}`}
                data-testid="footer-phone"
                className="opacity-90 hover:text-white"
              >
                {CONTACT.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="mt-1 opacity-70" />
              <span className="opacity-90 leading-relaxed">{CONTACT.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={15} className="mt-1 opacity-70" />
              <a
                href={`mailto:contact@adhdschool.in`}
                className="opacity-90 hover:text-white"
              >
                contact@adhdschool.in
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="adhd-container py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs opacity-70">
          <p>
            © {new Date().getFullYear()} {CONTACT.schoolName}. {t("footer.copyright")}
          </p>
          <p className="opacity-80">{CONTACT.physioName}</p>
        </div>
        <div className="adhd-container pb-5 -mt-1 text-center text-[11px] tracking-wide opacity-50">
          <span data-testid="footer-credit">
            Developed by <span className="font-semibold opacity-90">S Web Solution</span>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
